// src/pages/Profile/FamilyMember/CreateFamilyMember.js
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  insertFamilyMember,
  getAllRelations,
} from "../../../services/Profile/FamilyMember/CreateFamilyMember";
import BloodGroupDropdown from "../../../services/Profile/FamilyMember/BloodGroupDropdown";

export default function CreateFamilyMember({ onCancel, onSuccess }) {
  const user = JSON.parse(localStorage.getItem("medarphaUser") || "{}");
  const userId = user?.id || user?.userId;

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    age: "",
    relationId: "",
    gender: "",
    dateOfBirth: "",
    heightCM: "",
    weightKG: "",
    bloodGroup: "",
    address: "",
    isDefault: false,
    photo: null,
  });

  const [relations, setRelations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState(null);

  useEffect(() => {
    const fetchRelations = async () => {
      try {
        const data = await getAllRelations();
        setRelations(data || []);
      } catch (err) {
        toast.error("Failed to fetch relations");
      }
    };
    fetchRelations();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === "file") {
      setFormData((prev) => ({ ...prev, photo: files[0] }));
      setPreviewPhoto(URL.createObjectURL(files[0]));
    } else if (name === "dateOfBirth") {
      const today = new Date();
      const dob = new Date(value);
      let age = today.getFullYear() - dob.getFullYear();
      const m = today.getMonth() - dob.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;

      setFormData((prev) => ({
        ...prev,
        dateOfBirth: value,
        age: age >= 0 ? age : "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userId) return toast.error("User ID not found");

    setLoading(true);

    try {
      const res = await insertFamilyMember(formData, userId);

      if (res?.succeeded) {
        toast.success(res.message || "Family member added successfully");

        setFormData({
          firstName: "",
          lastName: "",
          age: "",
          relationId: "",
          gender: "",
          dateOfBirth: "",
          heightCM: "",
          weightKG: "",
          bloodGroup: "",
          address: "",
          isDefault: false,
          photo: null,
        });
        setPreviewPhoto(null);
        onSuccess && onSuccess();
      } else {
        toast.error(res?.message || "Insert failed");
      }
    } catch {
      toast.error("Insert failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex justify-center py-6 px-3">
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-lg p-6">

        {/* HEADER */}
        <h2 className="text-2xl font-bold text-gray-800 mb-6">
          Add Family Member
        </h2>

        <form onSubmit={handleSubmit}>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* First Name */}
            <input
              name="firstName"
              placeholder="First Name"
              value={formData.firstName}
              onChange={handleChange}
              className="input"
              required
            />

            {/* Last Name */}
            <input
              name="lastName"
              placeholder="Last Name"
              value={formData.lastName}
              onChange={handleChange}
              className="input"
            />

            {/* Age */}
            <input
              name="age"
              type="number"
              placeholder="Age"
              value={formData.age}
              readOnly
              className="input bg-gray-100"
            />

            {/* Relation */}
            <select
              name="relationId"
              value={formData.relationId}
              onChange={handleChange}
              className="input"
              required
            >
              <option value="">Select Relation</option>
              {relations.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.relationName}
                </option>
              ))}
            </select>

            {/* Gender */}
            <select
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              className="input"
            >
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>

            {/* DOB */}
            <input
              type="date"
              name="dateOfBirth"
              value={formData.dateOfBirth}
              onChange={handleChange}
              className="input"
            />

            {/* Height */}
            <input
              name="heightCM"
              type="number"
              placeholder="Height (CM)"
              value={formData.heightCM}
              onChange={handleChange}
              className="input"
            />

            {/* Weight */}
            <input
              name="weightKG"
              type="number"
              placeholder="Weight (KG)"
              value={formData.weightKG}
              onChange={handleChange}
              className="input"
            />

            {/* Blood Group */}
            <div className="md:col-span-1">
              <BloodGroupDropdown
                value={formData.bloodGroup}
                onChange={handleChange}
              />
            </div>

            {/* Address */}
            <input
              name="address"
              placeholder="Address"
              value={formData.address}
              onChange={handleChange}
              className="input md:col-span-1"
            />

            {/* Photo */}
            <div className="md:col-span-2">
              <input type="file" onChange={handleChange} className="input" />

              {previewPhoto && (
                <img
                  src={previewPhoto}
                  alt="preview"
                  className="mt-3 w-24 h-24 rounded-xl object-cover border"
                />
              )}
            </div>

            {/* Checkbox */}
            <div className="md:col-span-2 flex items-center gap-2">
              <input
                type="checkbox"
                name="isDefault"
                checked={formData.isDefault}
                onChange={handleChange}
              />
              <span className="text-sm text-gray-600">
                Set as default
              </span>
            </div>

          </div>

          {/* 🔥 STICKY BUTTONS */}
          <div className="sticky bottom-0 bg-white pt-4 mt-6 flex justify-end gap-3 border-t">

            <button
              type="button"
              onClick={onCancel}
              className="px-5 py-2 rounded-lg border text-gray-600 hover:bg-gray-100 transition"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 rounded-lg bg-gradient-to-r from-red-500 to-pink-500 text-white font-semibold shadow hover:opacity-90 active:scale-95 transition"
            >
              {loading ? "Saving..." : "Save"}
            </button>

          </div>
        </form>
      </div>

      {/* INPUT COMMON STYLE */}
      <style>{`
        .input {
          width: 100%;
          padding: 10px 12px;
          border-radius: 10px;
          border: 1px solid #e5e7eb;
          outline: none;
          transition: 0.2s;
        }
        .input:focus {
          border-color: #ef4444;
          box-shadow: 0 0 0 2px rgba(239,68,68,0.1);
        }
      `}</style>
    </div>
  );
}